import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

export const useStyles = makeStyles()((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    rootAutoHeight: {
        flex: 'none',
        minHeight: 'auto',
    },
    // Synthetic horizontal scrollbar rendered above the table; its scrollLeft
    // is mirrored onto the body table via translateX so we get a top scroller.
    topScrollbar: {
        overflowX: 'auto',
        overflowY: 'hidden',
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px 4px 0 0',
        '& > div': {
            height: 1,
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.action.active} transparent`,
        '&::-webkit-scrollbar': {
            height: 8,
            width: 8,
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.disabled,
            borderRadius: 4,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            '&:hover': {
                backgroundColor: theme.palette.action.active,
            },
        },
    },
    tableScrollArea: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    tableScrollAreaAutoHeight: {
        flex: 'none',
        minHeight: 'auto',
    },
    headerViewport: {
        flexShrink: 0,
        overflow: 'hidden',
        backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px 4px 0 0',
    },
    // Applied when the synthetic top scrollbar already provides the top
    // border + radius, to avoid doubling them up.
    headerViewportFlat: {
        borderTop: 'none',
        borderRadius: 0,
    },
    // Outer border wrapper for the body area; clips the scrollbar that
    // extends past the border via the negative margin on bodyViewport.
    bodyBorderWrap: {
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRadius: '0 0 4px 4px',
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column' as const,
    },
    bodyBorderWrapAutoHeight: {
        flex: 'none',
        minHeight: 'auto',
    },
    bodyViewport: {
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        // Disable scroll anchoring (conflicts with the table virtualizer).
        overflowAnchor: 'none',
        flex: 1,
        minHeight: 0,
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.action.active} transparent`,
        '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.disabled,
            borderRadius: 4,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            '&:hover': {
                backgroundColor: theme.palette.action.active,
            },
        },
    },
    // Applied during initial skeleton render; the skeleton is taller than the
    // viewport so we clip rather than let it push the flex container.
    bodyViewportLoading: {
        overflowY: 'hidden',
    },
    table: {
        tableLayout: 'fixed',
        // Promote to a compositor layer for the horizontal-pan translateX.
        willChange: 'transform',
    },
    cell: {
        padding: '8px 12px 8px 4px',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        borderRight: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
        '&:last-of-type': { borderRight: 'none' },
    },
    cellFirst: {
        paddingLeft: '12px',
    },
    cellNoBorderRight: {
        borderRight: 'none',
    },
    headerCell: {
        userSelect: 'none',
        backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
        fontWeight: 600,
        '&.MuiTableCell-root': {
            cursor: 'pointer',
        },
        '&:hover': {
            backgroundColor:
                theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
        },
    },
    headerCellContent: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
    },
    headerSortIcon: {
        fontSize: '0.875rem',
        opacity: 0.7,
        marginLeft: '2px',
    },
    headerChevron: {
        fontSize: '1rem',
        opacity: 0,
        transition: theme.transitions.create('opacity', {
            duration: theme.transitions.duration.shortest,
        }),
        '.MuiTableCell-root:hover &': {
            opacity: 1,
        },
    },
    headerChevronVisible: {
        opacity: 0.7,
    },
    headerFilterIcon: {
        fontSize: '0.875rem',
        color: theme.palette.primary.main,
        marginLeft: '2px',
    },
    cellText: {
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        cursor: 'pointer',
        userSelect: 'none',
        borderRadius: '2px',
        padding: '0 2px',
        margin: '0 -2px',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    cellLink: {
        color: theme.palette.primary.main,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    cellWrap: {
        whiteSpace: 'normal',
        overflow: 'visible',
    },
    cellTextWrap: {
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        wordBreak: 'break-word',
    },
    emptyStateCell: {
        border: 'none',
        padding: '48px 0',
        textAlign: 'center',
    },
    loadingMoreCell: {
        border: 'none',
        padding: '12px 0',
        textAlign: 'center',
    },
    loadingMoreInner: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    searchInput: {
        flexShrink: 0,
        minWidth: 220,
    },
    tableFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: '6px',
    },
    totalLabel: {
        flexShrink: 0,
        alignSelf: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    },
    filterBar: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '8px 0',
        maxHeight: '120px',
        overflowY: 'auto',
        flexShrink: 0,
        backgroundColor: theme.palette.background.default,
    },
    filterPopoverContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 12px',
        minWidth: '180px',
    },
    filterSelectAll: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: '4px',
        paddingBottom: '4px',
    },
    resizeHandle: {
        position: 'absolute',
        top: 0,
        right: '-3px',
        width: '6px',
        height: '100%',
        cursor: 'col-resize',
        zIndex: 2,
        userSelect: 'none',
        touchAction: 'none',
        backgroundColor: 'transparent',
        transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    resizeHandleActive: {
        backgroundColor: theme.palette.primary.main,
    },
    row: {
        cursor: 'default',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    rowClickable: {
        cursor: 'pointer',
    },
    rowExpandable: {
        cursor: 'pointer',
    },
    rowExpanded: {
        backgroundColor: theme.palette.action.selected,
        '&:hover': {
            backgroundColor: theme.palette.action.selected,
        },
    },
    rowHighlighted: {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        // box-shadow on <tr> is unreliable in border-collapse tables; apply the
        // left accent bar on the first <td> instead — always renders correctly.
        '& td:first-child': {
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
        },
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.18),
        },
    },
    expandCell: {
        padding: '0 0 0 4px',
        borderRight: 'none',
    },
    expandIcon: {
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandIconOpen: {
        transform: 'rotate(90deg)',
    },
    expandedContentRow: {
        '& > td': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    expandedContentCell: {
        padding: 0,
        border: 'none',
    },
    expandedContent: {
        padding: theme.spacing(2),
        backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
    },
    expandCellSize: {
        width: 40,
        minWidth: 40,
        maxWidth: 40,
    },
}));
